import { api } from "@tt-app/api";
import {
  VUEX_FORM_MUTATION_NAMES,
  VUEX_FORM_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_MUTATION_NAMES
} from "@tt-app/constants";
import {
  AUTH_TOKEN_LS_KEY,
  PayloadPwdChngReset,
  RespPwdChngReset,
  ErrPwdChngReset
} from "@tt-app/models";
import { Alias, UserActionContext } from "./types";
import { getAuthInfo, validatePassword, MainLogger } from "@tt-app/utils";
import { AxiosError } from "axios";
import { Profile } from "@tt-app/types";
import router from "@tt-app/router";
import {
  TerratalkLiveActionNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";

const {
  ALIASES_REQUEST,
  COURSE_HISTORY_REQUEST,
  GROUPS_REQUEST,
  LOAD_LOCAL_STORAGE_DATA_REQUEST,
  LOAD_USER_PROFILE,
  LOGIN_REQUEST,
  LOGIN_SSO,
  PASSWORD_RESET,
  SELECT_ALIAS,
  SET_PASSWORD_WITH_TMP_PASSWORD_REQUEST,
  SET_PASSWORD_WITH_TOKEN_REQUEST,
  UPDATE_USER_PROFILE,
  VALIDATE_TMP_PASSWORD_REQUEST,
  VALIDATE_JWT_REQUEST,
  VALIDATE_RESET_PASSWORD_JWT_REQUEST
} = VUEX_USER_ACTION_NAMES;

const {
  ONE_SIGNAL_DELETE_ALL_TAGS,
  ONE_SIGNAL_GET_CTA_INTERACTION_LOG,
  ONE_SIGNAL_INIT,
  ONE_SIGNAL_REMOVE_EXTERNAL_USER_ID,
  ONE_SIGNAL_SEND_TAGS,
  ONE_SIGNAL_SET_EXTERNAL_USER_ID,
  ONE_SIGNAL_SET_SUBSCRIPTION,
  PUSH_NOTIFICATION_TOAST
} = VUEX_ROOT_ACTION_NAMES;

const {
  ALIASES,
  COURSE_HISTORY,
  GROUPS,
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  LOGOUT,
  SELECTED_ALIAS,
  SET_PASSWORD_WITH_TMP_PASSWORD,
  SET_PASSWORD_RESET_ERROR,
  SET_USER_PROFILE,
  VALIDATE_TMP_PASSWORD,
  VALIDATE_JWT_FAILURE,
  VALIDATED_JWT,
  VALIDATE_RESET_PASSWORD_JWT_FAILURE
} = VUEX_USER_MUTATION_NAMES;

const { UPDATE_FORM } = VUEX_FORM_MUTATION_NAMES;

const userActions = {
  async [ALIASES_REQUEST]({ commit, state }: any) {
    const {
      authentication: { token }
    } = state;

    try {
      const response = await api.user.userAliases(token);

      const { data: aliases } = response;

      commit(ALIASES, aliases);
    } catch (error) {
      console.log(`Error - request_aliases action: ${error}`);
    }
  },

  async [COURSE_HISTORY_REQUEST]({ commit, rootGetters }: any, { courseUuid }: any) {
    try {
      commit(COURSE_HISTORY, { history: undefined });

      const authInfo = await getAuthInfo(rootGetters);

      const { data: history } = await api.user.courseHistory({
        authInfo,
        courseUuid
      });

      const exercisesByUuid: { [index: string]: any } = {};

      // transform exercises into an object with the uuid as the key
      // in order to simplify looking up specific exercises
      history.exercises.forEach((exercise: any) => {
        exercisesByUuid[exercise.uuid] = exercise;
      });

      history.exercises = exercisesByUuid;

      commit(COURSE_HISTORY, { history });
    } catch (error) {
      console.log({ error });
    }
  },

  async [GROUPS_REQUEST]({ commit, rootGetters }: any) {
    try {
      commit(GROUPS, undefined);

      const authInfo = await getAuthInfo(rootGetters);

      const response = await api.user.groups({ authInfo });

      const { data: groups } = response;

      commit(GROUPS, groups);
    } catch (error) {
      console.log("groups_request error", error);
    }
  },

  async [LOAD_LOCAL_STORAGE_DATA_REQUEST]({ commit }: any) {
    const token = localStorage.getItem(AUTH_TOKEN_LS_KEY);

    if (token) {
      commit(LOGIN_SUCCESS, { token });
    }
  },

  async [LOAD_USER_PROFILE]({ commit, dispatch, state }: UserActionContext) {
    try {
      const selectedAlias = state.selectedAlias;

      let aliasUuid;
      if (!selectedAlias) {
        aliasUuid = state.aliases[0].uuid;
      } else {
        aliasUuid = state.selectedAlias?.uuid as string;
      }

      const authInfo = {
        token: state.authentication.token as string,
        aliasUuid
      };

      const profile = await api.user.getProfile(authInfo);

      commit(SET_USER_PROFILE, profile);

      await dispatch(SELECT_ALIAS);

      dispatch(
        `${TerratalkLiveNamespace}/${TerratalkLiveActionNames.REQUEST_STATE}`,
        {},
        { root: true }
      );

      await dispatch(ONE_SIGNAL_GET_CTA_INTERACTION_LOG, {}, { root: true });
      await dispatch(ONE_SIGNAL_INIT, {}, { root: true });
      await dispatch(ONE_SIGNAL_SET_EXTERNAL_USER_ID, {}, { root: true });
      await dispatch(ONE_SIGNAL_SEND_TAGS, {}, { root: true });
      await dispatch(ONE_SIGNAL_SET_SUBSCRIPTION, undefined, { root: true });
    } catch (err) {
      console.warn("Error: ", err);
      throw err;
    }
  },

  async [LOGIN_REQUEST]({ commit, dispatch, rootState }: any, payload: any) {
    const keychain = (window as any).Keychain;

    commit(LOGIN_FAILURE, "");
    const email = rootState.form.authentication.email.value;
    const password = payload.password;
    const userType = "individual";

    const loginPayload = {
      email,
      password
    };

    try {
      const response =
        userType === "individual"
          ? await api.user.individualLogin(loginPayload)
          : await api.user.organizationLogin(loginPayload);

      const {
        data: { token }
      } = response;

      if (keychain) {
        keychain.saveCredentials(email, password);
      }

      commit(LOGIN_SUCCESS, { token });
    } catch (error) {
      commit(
        `${VUEX_FORM_NAMESPACE}/${UPDATE_FORM}`,
        {
          formName: "authentication",
          field: "tmpPassword",
          value: password
        },
        {
          root: true
        }
      );
      await dispatch(VALIDATE_TMP_PASSWORD_REQUEST);
    }
  },

  async [LOGIN_SSO]({ commit }: UserActionContext, token: string) {
    commit(LOGIN_FAILURE, "");

    try {
      const authToken = await api.user.validateSsoToken(token);
      console.log("Auth token", authToken);

      commit(LOGIN_SUCCESS, { token: authToken });
    } catch (err) {
      const axiosErr = err as AxiosError;
      // Invalid SSO Token
      if (axiosErr.response && axiosErr.response.status === 400) {
        MainLogger.warn(`Invalid SSO token: ${token}`);
        commit(LOGIN_FAILURE, err);
      } else {
        MainLogger.warn(`SSO Validation error`, err);
      }
    }
  },

  [VUEX_ROOT_ACTION_NAMES.LOGOUT_REQUEST]: {
    root: true,
    async handler({ commit, dispatch }: any) {
      dispatch(`${VUEX_ROOT_ACTION_NAMES.CLEAR_LOCAL_STORAGE}`, {}, { root: true });

      commit(LOGOUT);

      dispatch(ONE_SIGNAL_DELETE_ALL_TAGS, {}, { root: true });
      dispatch(ONE_SIGNAL_REMOVE_EXTERNAL_USER_ID, {}, { root: true });
      dispatch(ONE_SIGNAL_SET_SUBSCRIPTION, false, { root: true });
    }
  },

  async [PASSWORD_RESET]({ commit }: UserActionContext, payload: PayloadPwdChngReset) {
    try {
      const errorKey = validatePassword(payload.password);
      if (errorKey) throw new Error(errorKey);

      const resp = await api.user.pwdChngReset({
        token: payload.token,
        password: payload.password
      });

      const respOk = resp as RespPwdChngReset;
      if (respOk.authToken) {
        commit(LOGIN_SUCCESS, { token: respOk.authToken });
        commit(SET_PASSWORD_RESET_ERROR, "");
        return;
      }

      const respErr = resp as ErrPwdChngReset;
      if (respErr.password) {
        commit(SET_PASSWORD_RESET_ERROR, respErr.password[0]);
      }
    } catch (err) {
      commit(SET_PASSWORD_RESET_ERROR, err.message);
    }
  },

  async [SELECT_ALIAS]({ commit, dispatch, state }: UserActionContext, payload?: { alias: Alias }) {
    let mustReloadProfile = false;

    const userUuid = state?.profile?.userUuid || "";
    const savedAliasUuids = JSON.parse(window.localStorage.getItem("selected_aliases") || "{}");
    const savedAliasUuid = savedAliasUuids[userUuid];
    const savedAlias = state.aliases.find(alias => {
      return alias.uuid === savedAliasUuid;
    });

    let newAlias;
    let routeAliasUuid: string | undefined;
    let routeAlias: Alias | undefined;
    if (payload) {
      newAlias = payload.alias;

      mustReloadProfile = savedAlias?.uuid !== newAlias.uuid;
    } else {
      routeAliasUuid = router.app.$route.query.aliasUuid as string;

      routeAlias = state.aliases.find(alias => {
        return alias.uuid === routeAliasUuid;
      });

      if (routeAliasUuid && !routeAlias) {
        await dispatch(VUEX_ROOT_ACTION_NAMES.LOGOUT_REQUEST);

        return;
      }

      newAlias = routeAlias || savedAlias || state.aliases[0];

      const selectedAliasUuid = state.selectedAlias?.uuid;
      mustReloadProfile = newAlias.uuid !== selectedAliasUuid;
    }

    const updatedSavedAliasUuids = { ...savedAliasUuids, [userUuid]: newAlias.uuid };
    const updatedSavedAliasUuidsString = JSON.stringify(updatedSavedAliasUuids);
    window.localStorage.setItem("selected_aliases", updatedSavedAliasUuidsString);

    commit(SELECTED_ALIAS, { alias: newAlias });

    if (mustReloadProfile) {
      await dispatch(LOAD_USER_PROFILE);

      if (routeAliasUuid && routeAlias) {
        const notificationText = (this._vm as any).$__(
          "alias.change.notification",
          routeAlias.name
        );

        dispatch(
          PUSH_NOTIFICATION_TOAST,
          { text: notificationText, timespan: 3000 },
          { root: true }
        );
      }
    }
  },

  async [SET_PASSWORD_WITH_TMP_PASSWORD_REQUEST]({ commit, rootState }: any, payload: any) {
    const keychain = (window as any).Keychain;

    try {
      const email = rootState.form.authentication.email.value;
      const tmpPassword = rootState.form.authentication.tmpPassword.value;
      const { password } = payload;

      const authDetails = {
        email,
        password,
        tmpPassword
      };

      const errorKey = validatePassword(password);
      if (errorKey) throw new Error(errorKey);

      const response = await api.user.setPasswordWithTmpPassword(authDetails);

      const {
        data: { token }
      } = response;

      if (keychain) {
        keychain.saveCredentials(email, password);
      }

      commit(SET_PASSWORD_WITH_TMP_PASSWORD, { token });
    } catch (error) {
      if (error.response) {
        commit(LOGIN_FAILURE, error.response.data.password[0]);
      }
      commit(LOGIN_FAILURE, error.message);
    }
  },

  async [SET_PASSWORD_WITH_TOKEN_REQUEST](
    { commit }: any,
    payload: { password: string; token: string }
  ) {
    try {
      const errorKey = validatePassword(payload.password);
      if (errorKey) throw new Error(errorKey);

      const response = await api.user.setPasswordWithToken(payload);

      const {
        data: { authToken }
      } = response;

      commit(LOGIN_SUCCESS, { token: authToken });
    } catch (error) {
      if (error.response) {
        commit(LOGIN_FAILURE, error.response.data.password[0]);
      }
      commit(LOGIN_FAILURE, error.message);
    }
  },

  /**
   * TBC: try/catch here or at caller level?
   * @param param0
   * @param newSettings
   */
  async [UPDATE_USER_PROFILE]({ commit, rootGetters }: UserActionContext, newSettings: Profile) {
    const authInfo = await getAuthInfo(rootGetters);

    if (authInfo) {
      const profile = await api.user.updateProfile(authInfo, newSettings);
      commit(SET_USER_PROFILE, profile);
    } else {
      throw new Error("No authentication info found");
    }
  },

  async [VALIDATE_TMP_PASSWORD_REQUEST]({ commit, rootState }: any) {
    try {
      const validationPayload = {
        email: rootState.form.authentication.email.value,
        tmpPassword: rootState.form.authentication.tmpPassword.value
      };

      const response = await api.user.authWithTmpPassword(validationPayload);

      const { data } = response;
      commit(VALIDATE_TMP_PASSWORD, data);
    } catch (error) {
      commit(
        `${VUEX_FORM_NAMESPACE}/${UPDATE_FORM}`,
        {
          formName: "authentication",
          field: "tmpPassword",
          value: ""
        },
        {
          root: true
        }
      );
      commit(LOGIN_FAILURE, "user.authentication.error.incorrectDetails");
    }
  },

  async [VALIDATE_JWT_REQUEST]({ commit }: any, { jwt }: any) {
    try {
      const response = await api.user.validateJwt({ jwt });

      const {
        data: { authToken, email, orgName, status, userName }
      } = response;

      switch (status) {
        case "VALID":
          commit(LOGIN_SUCCESS, { token: authToken });
          break;

        case "REQUIRED_TO_SET_PASSWORD":
          commit(VALIDATED_JWT, { email, orgName, userName });
          break;
        default:
          throw new Error("VALIDATE_JWT_REQUEST: response status does not match any known status");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        switch (error.response.data.token[0]) {
          case "INVALID":
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.invalid");
            break;
          case "EXPIRED":
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.expired");
            break;
          default:
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.error");
        }
      }
      console.log({ error });
    }
  },

  async [VALIDATE_RESET_PASSWORD_JWT_REQUEST]({ commit }: any, { jwt }: any) {
    try {
      const response = await api.user.validateResetPasswordJwt({ jwt });

      const {
        data: { status }
      } = response;

      switch (status) {
        case "INVALID":
          commit(VALIDATE_RESET_PASSWORD_JWT_FAILURE, "user.authentication.jwt.invalid");
          break;

        default:
          throw new Error("VALIDATE_JWT_REQUEST: response status does not match any known status");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        switch (error.response.data.token[0]) {
          case "INVALID":
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.invalid");
            break;
          case "EXPIRED":
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.expired");
            break;
          default:
            commit(VALIDATE_JWT_FAILURE, "user.authentication.jwt.error");
        }
      }
      console.log({ error });
    }
  }
};

export { userActions };
